import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import Toast from '../components/common/Toast';
import './ToastContext.css';

interface ToastData {
  title: string;
  message: string;
  autoClose?: boolean;
  autoCloseDelay?: number;
}

interface ToastContextType {
  showToast: (toast: ToastData) => void;
  hideToast: () => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toast, setToast] = useState<ToastData | null>(null);
  const [toastKey, setToastKey] = useState(0);

  const showToast = useCallback((toastData: ToastData) => {
    // Replace any existing toast with the new one and increment key to force remount
    setToast(toastData);
    setToastKey(prev => prev + 1);
  }, []);

  const hideToast = useCallback(() => {
    setToast(null);
  }, []);

  return (
    <ToastContext.Provider value={{ showToast, hideToast }}>
      {children}
      {toast && (
        <div className="toast-container">
          <Toast
            key={toastKey}
            title={toast.title}
            message={toast.message}
            onClose={hideToast}
            autoClose={toast.autoClose !== undefined ? toast.autoClose : true}
            autoCloseDelay={toast.autoCloseDelay || 3000}
          />
        </div>
      )}
    </ToastContext.Provider>
  );
};

